import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../../api.ts';
import { TopBar } from '../../components/TopBar.tsx';
import { TrustBadges } from '../../components/TrustBadges.tsx';
import { RequirementsList } from '../../components/RequirementsList.tsx';
import { MatchBadge } from '../../components/MatchBadge.tsx';
import { ProLine } from '../../components/ProSummary.tsx';
import { RatingForm } from '../../components/RatingForm.tsx';
import { RatingLine } from '../../components/RatingLine.tsx';
import { IncidentPanel } from '../../components/IncidentPanel.tsx';
import { HoursPanel } from '../../components/HoursPanel.tsx';
import { PaymentStatus } from '../../components/PaymentStatus.tsx';
import { ReliabilityBadges } from '../../components/ReliabilityBadges.tsx';
import { formatDateRange, formatEuros, MISSION_STATUS_LABELS } from '../../format.ts';
import type { Application, Mission } from '../../types.ts';
import { Avatar } from '../../components/Avatar.tsx';
import { BriefingPanel } from '../../components/BriefingPanel.tsx';
import { openConversation } from '../../messages.ts';

export function RestoMissionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [mission, setMission] = useState<Mission | null>(null);
  const [applications, setApplications] = useState<Application[]>([]);
  const [busy, setBusy] = useState(false);

  async function load() {
    const res = await api.get<{ mission: Mission }>(`/missions/${id}`);
    setMission(res.mission);
    if (res.mission.status === 'PUBLISHED' || res.mission.status === 'MATCHING') {
      const appsRes = await api.get<{ applications: Application[] }>(`/missions/${id}/applications`);
      setApplications(appsRes.applications);
    } else {
      setApplications([]);
    }
  }

  useEffect(() => {
    load();
  }, [id]);

  async function writeTo(extraId: string) {
    setBusy(true);
    try {
      navigate(`/resto/messages/${await openConversation(id!, extraId)}`);
    } finally {
      setBusy(false);
    }
  }

  async function acceptApplication(applicationId: string) {
    setBusy(true);
    try {
      await api.post(`/missions/${id}/applications/${applicationId}/accept`);
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function validateMission() {
    setBusy(true);
    try {
      await api.post(`/missions/${id}/validate`);
      await load();
    } finally {
      setBusy(false);
    }
  }

  const [payError, setPayError] = useState<string | null>(null);
  const [needsReauthorization, setNeedsReauthorization] = useState(false);

  async function captureAndPay() {
    setBusy(true);
    setPayError(null);
    try {
      await api.post(`/payments/missions/${id}/payments/capture`);
      setNeedsReauthorization(false);
      await load();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : '';
      if (code === 'REAUTHORIZATION_REQUIRED') {
        setNeedsReauthorization(true);
        setPayError('Les heures retenues dépassent le montant autorisé : réautorisez le paiement au montant exact pour continuer.');
      } else if (code === 'HOURS_DISPUTE_OPEN') {
        setPayError('Les heures sont contestées : le paiement reprendra dès que le ticket sera réglé.');
      } else if (code === 'EXTRA_STRIPE_ACCOUNT_MISSING') {
        setPayError('L’extra n’a pas encore configuré son compte de paiement.');
      } else {
        setPayError('Le paiement n’a pas pu être déclenché.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function reauthorize() {
    setBusy(true);
    setPayError(null);
    try {
      await api.post(`/payments/missions/${id}/reauthorize`);
      setNeedsReauthorization(false);
      await captureAndPay();
    } catch {
      setPayError('La nouvelle autorisation a échoué. Vérifiez la configuration du paiement.');
      setBusy(false);
    }
  }

  if (!mission) {
    return (
      <div className="app-shell">
        <TopBar title="Mission" back />
        <div className="screen-content empty-state">Chargement…</div>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <TopBar title={mission.title} back />
      <div className="screen-content">
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span className="badge badge-neutral">{MISSION_STATUS_LABELS[mission.status] ?? mission.status}</span>
            <span style={{ fontWeight: 800 }}>{formatEuros(mission.hourly_rate_cents)}/h</span>
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{formatDateRange(mission.starts_at, mission.ends_at)}</div>
          {mission.assigned_first_name && (
            <div style={{ fontSize: 13.5, fontWeight: 700 }}>
              Extra assigné : {mission.assigned_first_name} {mission.assigned_last_name}
              {mission.assigned_extra_id && <> <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => writeTo(mission.assigned_extra_id!)}>Écrire</button></>}
            </div>
          )}
        </div>

        <RequirementsList requirements={mission.requirements} coversCount={mission.covers_count} teamMode={mission.team_mode} perspective="resto" />

        {mission.briefing && (
          <BriefingPanel
            missionId={mission.id}
            organizationId={mission.organization_id}
            startsAt={mission.starts_at}
            briefing={mission.briefing}
            editable={Boolean(mission.briefingEditable)}
            onChanged={load}
          />
        )}

        {(mission.status === 'PUBLISHED' || mission.status === 'MATCHING') && (
          <>
            <Link to={`/resto/missions/${id}/search`} className="btn btn-secondary">Rechercher un extra à contacter</Link>
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, padding: '0 2px 8px' }}>Candidatures reçues</div>
              {applications.length === 0 && <div className="empty-state">Aucune candidature pour l'instant.</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {applications.map((a) => (
                  <div key={a.id} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                    <Avatar avatar={a.avatar} size={48} label={`${a.first_name} ${a.last_name}`} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700 }}>{a.first_name} {a.last_name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {a.missions_completed} missions
                      </div>
                      <ProLine pro={a.pro} />
                      <RatingLine rating={a.rating} />
                      <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <MatchBadge match={a.match} perspective="resto" />
                        <ReliabilityBadges reliability={a.reliability} />
                        <TrustBadges badges={a.badges} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'stretch' }}>
                      {a.status === 'PENDING' ? (
                        <button className="btn btn-primary" style={{ width: 'auto', padding: '8px 14px' }} onClick={() => acceptApplication(a.id)} disabled={busy}>
                          Accepter
                        </button>
                      ) : (
                        <span className="badge badge-available">Acceptée</span>
                      )}
                      <button className="btn btn-secondary btn-sm" onClick={() => writeTo(a.extra_id)} disabled={busy}>Écrire</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {mission.status === 'ASSIGNED' && (
          <button className="btn btn-primary" onClick={() => navigate(`/resto/missions/${id}/payment`)}>
            Confirmer et payer
          </button>
        )}

        {mission.status === 'CONFIRMED' && (
          <div className="badge badge-pending" style={{ justifyContent: 'center' }}>En attente du jour de la mission</div>
        )}

        {mission.status === 'IN_PROGRESS' && (
          <div className="badge badge-pending" style={{ justifyContent: 'center' }}>Mission en cours</div>
        )}

        {mission.status === 'COMPLETED' && (
          <button className="btn btn-primary" onClick={validateMission} disabled={busy}>
            Valider la mission
          </button>
        )}

        {mission.payment && mission.status !== 'CLOSED' && <PaymentStatus missionId={mission.id} payment={mission.payment} onChanged={load} />}

        {mission.assigned_extra_id && <HoursPanel missionId={mission.id} side="ORG" status={mission.status} onChanged={load} />}

        {mission.status === 'VALIDATED' && (
          <>
            {payError && <div className="error-banner">{payError}</div>}
            {needsReauthorization ? (
              <button className="btn btn-primary" onClick={reauthorize} disabled={busy}>Réautoriser le paiement au montant exact</button>
            ) : (
              <button className="btn btn-primary" onClick={captureAndPay} disabled={busy}>Déclencher le paiement à l'extra</button>
            )}
          </>
        )}

        {mission.assigned_extra_id && ['ASSIGNED', 'CONFIRMED', 'IN_PROGRESS', 'COMPLETED', 'VALIDATED', 'CLOSED'].includes(mission.status) && (
          <IncidentPanel missionId={mission.id} startsAt={mission.starts_at} endsAt={mission.ends_at} />
        )}

        {(mission.status === 'VALIDATED' || mission.status === 'CLOSED') && mission.assigned_extra_id && <RatingForm missionId={mission.id} />}
      </div>
    </div>
  );
}
